/**
 * DepositsSection - Deposit management list
 *
 * REFACTORED (Phase 8.1): Uses Zod types from @club-bot/shared/contracts
 * - AdminDepositSummary for list items
 * - AdminDepositDetail for detail view
 * - Uses referenceNumber as primary identifier (not numeric id)
 *
 * Uses shared components:
 * - ListLayout (layouts) - page shell
 * - FilterDropdowns/useFilterPanel (core) - filter system
 * - SortButton (composed) - sort selector
 * - MenuButton (composed) - navigation
 * - InfoCard/InfoCardList (composed) - list items
 * - InfoCardDetail (composed) - box-based detail view
 * - BottomSheet (core) - detail container
 *
 * Detail View Boxes:
 * - Box 1: Deposit Information (static) - user, amount, bonus, dates
 * - Box 2: Service Details (static) - method, provider ID, receipt photo
 * - Box 3: Conversion Commission (conditional) - USDT, rate, commission
 * - Box 4: Force Complete Info (conditional) - only if force completed
 * - Box 5: Force Complete Action (conditional) - button when can force complete
 *
 * Features:
 * - Search by reference, telegram ID, amount
 * - Filter by status, payment method
 * - Sort by date, amount
 * - Infinite scroll list
 * - Tap to view details
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ArrowDownToLine,
  LayoutDashboard,
  Users,
  ArrowUpFromLine,
  FileText,
  Gift,
  Settings,
  Clock,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import {
  ListLayout,
  FilterDropdowns,
  useFilterPanel,
  InfoCard,
  InfoCardList,
  InfoCardDetail,
  BottomSheet,
  PopupMessage,
  cardMatchesSearch,
  findSearchHighlights,
} from '@/components/shared';
import type { FilterCategory, DetailBox } from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { SortOption } from '@/components/shared/composed/SortButton';
import { useInfiniteScroll } from '@/hooks';

// Import Zod types from shared contracts
import type { AdminDepositSummary, AdminDepositDetail } from '@club-bot/shared/contracts';
import { resolveMediaUrl } from '@club-bot/shared/contracts';

// Media service URL for resolving photo URLs
const MEDIA_SERVICE_URL = import.meta.env.VITE_MEDIA_SERVICE_URL || 'http://localhost:30003';

// ============================================
// BUILD BOXES FUNCTION
// ============================================

interface BuildDepositBoxesOptions {
  deposit: AdminDepositDetail;
  onForceCompleteClick?: () => void;
  forceCompleteLoading?: boolean;
  onEditFinancialsClick?: () => void;
  editLoading?: boolean;
}

/** Build deposit detail boxes from AdminDepositDetail data */
function buildDepositBoxes({
  deposit,
  onForceCompleteClick,
  forceCompleteLoading,
  onEditFinancialsClick,
  editLoading,
}: BuildDepositBoxesOptions): DetailBox[] {
  const isForceCompleted = deposit.forceCompleted ?? false;
  const totalWithBonus = deposit.amount + (deposit.bonusAmount ?? 0);

  // Resolve photo URL via media service
  const photoUrl = resolveMediaUrl(deposit.photoUrl, MEDIA_SERVICE_URL);

  return [
    // Box 1: Deposit Information (Static)
    {
      id: 'deposit-info',
      title: 'Deposit Information',
      fields: [
        { label: 'User ID', value: deposit.telegramId },
        { label: 'Username', value: deposit.username },
        { label: 'Deposit ID', value: deposit.referenceNumber },
        { label: 'Amount', value: deposit.amount, format: 'currency' },
        { label: 'Bonus %', value: deposit.bonusPercentage != null ? `${deposit.bonusPercentage}%` : null },
        { label: 'Bonus Amount', value: deposit.bonusAmount, format: 'currency' },
        { label: 'Total (Amount + Bonus)', value: totalWithBonus, format: 'currency' },
        { label: 'Started', value: deposit.createdAt, format: 'date' },
        { label: 'Finished', value: deposit.completedAt, format: 'date' },
      ],
      visible: true,
    },

    // Box 2: Service Details (Static)
    {
      id: 'service-details',
      title: 'Service Details',
      fields: [
        { label: 'Payment Method', value: deposit.paymentMethod },
        { label: 'Provider Hash', value: deposit.providerHash },
        { label: 'Provider Transaction ID', value: deposit.providerTransactionId },
      ],
      image: photoUrl
        ? { url: photoUrl, label: 'Check / Receipt' }
        : undefined,
      visible: true,
    },

    // Box 3: Conversion Commission (Conditional - only when conversion data exists)
    {
      id: 'conversion-commission',
      title: 'Conversion & Commission',
      fields: [
        { label: 'Amount (AZN)', value: deposit.amount, format: 'currency' },
        { label: 'USDT Amount', value: deposit.usdtAmount },
        { label: 'Exchange Rate', value: deposit.exchangeRate },
        { label: 'Commission %', value: deposit.commission != null ? `${deposit.commission}%` : null },
      ],
      actions: onEditFinancialsClick && deposit.status === 'completed' ? [
        {
          label: 'Edit Financials',
          onClick: onEditFinancialsClick,
          variant: 'secondary' as const,
          loading: editLoading,
        },
      ] : [],
      visible: deposit.usdtAmount != null || deposit.exchangeRate != null || deposit.commission != null || deposit.status === 'completed',
    },

    // Box 4: Force Complete Info (Only shows AFTER force complete happened)
    {
      id: 'force-complete-info',
      title: 'Force Complete',
      fields: [
        { label: 'Force Completed At', value: deposit.forceCompletedAt, format: 'date' },
        { label: 'Admin ID', value: deposit.forceCompletedBy },
        { label: 'Note', value: deposit.forceCompletedNote },
      ],
      visible: isForceCompleted,
    },

    // Box 5: Force Complete Action (Only shows when can force complete)
    {
      id: 'force-complete-action',
      fields: [], // No fields, just the action button
      actions: onForceCompleteClick && deposit.canForceComplete ? [
        {
          label: 'Force Complete',
          onClick: onForceCompleteClick,
          variant: 'success' as const,
          loading: forceCompleteLoading,
        },
      ] : [],
      visible: deposit.canForceComplete && !!onForceCompleteClick,
    },
  ];
}

// ============================================
// COMPONENT PROPS
// ============================================

export interface DepositsSectionProps {
  /** List of deposits (from Zod contract) */
  deposits: AdminDepositSummary[];
  /** Loading state */
  isLoading?: boolean;
  /** Whether there are more deposits to load */
  hasMore?: boolean;
  /** Load more callback */
  onLoadMore?: () => void;
  /** Search callback (for server-side search) */
  onSearch?: (query: string) => void;
  /** Filter callback (for server-side filter) */
  onFilter?: (filters: { status?: string[]; paymentMethod?: string[] }) => void;
  /** Sort callback (for server-side sort) */
  onSort?: (sort: string) => void;
  /** Get full deposit details by referenceNumber */
  onGetDepositDetails?: (referenceNumber: string) => Promise<AdminDepositDetail>;
  /** Force complete callback (by referenceNumber, with reason) */
  onForceComplete?: (referenceNumber: string, reason: string) => Promise<void>;
  /** Edit financials callback */
  onEditFinancials?: (referenceNumber: string, data: { usdtAmount?: number; exchangeRate?: number; commission?: number }) => Promise<void>;
  /** Navigate to other admin pages */
  onNavigate?: (page: string) => void;
  /** Available payment methods for filter */
  paymentMethods?: string[];
}

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'cascade', label: 'Cascade', icon: Settings },
  { value: 'referees', label: 'Referees', icon: Users },
];

// Sort options
const sortOptions: SortOption[] = [
  { value: 'newest', label: 'Newest First', icon: Clock },
  { value: 'oldest', label: 'Oldest First', icon: Clock },
  { value: 'amount_high', label: 'Amount (High to Low)', icon: TrendingUp },
  { value: 'amount_low', label: 'Amount (Low to High)', icon: TrendingDown },
];

// Default payment methods
const defaultPaymentMethods = ['fservice', 'nservice', 'eservice', 'cservice'];

// ============================================
// COMPONENT
// ============================================

export function DepositsSection({
  deposits,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  onSearch,
  onFilter,
  onSort,
  onGetDepositDetails,
  onForceComplete,
  onEditFinancials,
  onNavigate,
  paymentMethods = defaultPaymentMethods,
}: DepositsSectionProps) {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Sort state
  const [sortValue, setSortValue] = useState('newest');

  // Scroll container ref for infinite scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Build filter categories dynamically
  const filterCategories: FilterCategory[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'paid', label: 'Paid' },
          { value: 'completed', label: 'Completed' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'failed', label: 'Failed' },
        ],
      },
      {
        id: 'paymentMethod',
        label: 'Method',
        options: paymentMethods.map((m) => ({ value: m, label: m.toUpperCase() })),
      },
    ],
    [paymentMethods]
  );

  // Filter panel
  const filterPanel = useFilterPanel(filterCategories);

  // Detail sheet state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDeposit, setDetailDeposit] = useState<AdminDepositDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Force complete popup state
  const [forceCompletePopupOpen, setForceCompletePopupOpen] = useState(false);

  // Edit financials popup state
  const [editPopupOpen, setEditPopupOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormValues, setEditFormValues] = useState<{
    usdtAmount: string;
    exchangeRate: string;
    commission: string;
  }>({ usdtAmount: '', exchangeRate: '', commission: '' });

  // Infinite scroll - pass scroll container ref
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: onLoadMore || (() => {}),
    hasMore,
    isLoading,
    scrollRef: scrollContainerRef,
  });

  // Notify parent of filter changes
  useEffect(() => {
    onFilter?.({
      status: filterPanel.activeFilters.status,
      paymentMethod: filterPanel.activeFilters.paymentMethod,
    });
  }, [filterPanel.activeFilters, onFilter]);

  // Notify parent of sort changes
  useEffect(() => {
    onSort?.(sortValue);
  }, [sortValue, onSort]);

  // Filter deposits by search query (client-side)
  const filteredDeposits = useMemo(() => {
    if (!searchQuery) return deposits;
    return deposits.filter((deposit) =>
      cardMatchesSearch(
        {
          referenceNumber: deposit.referenceNumber,
          telegramId: deposit.telegramId,
          amount: String(deposit.amount),
        },
        searchQuery
      )
    );
  }, [deposits, searchQuery]);

  // Handle search
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      onSearch?.(query);
    },
    [onSearch]
  );

  // Handle sort
  const handleSort = useCallback((value: string) => {
    setSortValue(value);
  }, []);

  // Handle navigate to detail (by referenceNumber)
  const handleNavigateDetail = useCallback(
    async (referenceNumber: string) => {
      if (!onGetDepositDetails) return;

      setDetailLoading(true);
      setDetailOpen(true);

      try {
        const details = await onGetDepositDetails(referenceNumber);
        setDetailDeposit(details);
      } catch (error) {
        console.error('Failed to load deposit details:', error);
      } finally {
        setDetailLoading(false);
      }
    },
    [onGetDepositDetails]
  );

  // Open force complete popup
  const handleForceCompleteClick = useCallback(() => {
    setForceCompletePopupOpen(true);
  }, []);

  // Open edit financials popup (pre-fill with current values)
  const handleEditFinancialsClick = useCallback(() => {
    if (!detailDeposit) return;
    setEditFormValues({
      usdtAmount: detailDeposit.usdtAmount?.toString() ?? '',
      exchangeRate: detailDeposit.exchangeRate?.toString() ?? '',
      commission: detailDeposit.commission?.toString() ?? '',
    });
    setEditPopupOpen(true);
  }, [detailDeposit]);

  // Handle edit financials confirmation
  const handleEditFinancialsConfirm = useCallback(async () => {
    if (!detailDeposit || !onEditFinancials) return;

    const data: { usdtAmount?: number; exchangeRate?: number; commission?: number } = {};
    if (editFormValues.usdtAmount) data.usdtAmount = parseFloat(editFormValues.usdtAmount);
    if (editFormValues.exchangeRate) data.exchangeRate = parseFloat(editFormValues.exchangeRate);
    if (editFormValues.commission) data.commission = parseFloat(editFormValues.commission);

    // At least one field must have a value
    if (!data.usdtAmount && !data.exchangeRate && !data.commission) return;

    setEditPopupOpen(false);
    setEditLoading(true);
    try {
      await onEditFinancials(detailDeposit.referenceNumber, data);
      // Refresh detail after action
      if (onGetDepositDetails) {
        const updated = await onGetDepositDetails(detailDeposit.referenceNumber);
        setDetailDeposit(updated);
      }
    } catch (error) {
      console.error('Failed to edit deposit financials:', error);
    } finally {
      setEditLoading(false);
    }
  }, [detailDeposit, onEditFinancials, onGetDepositDetails, editFormValues]);

  // Handle force complete confirmation (with reason from popup)
  const handleForceCompleteConfirm = useCallback(async (data: { text?: string }) => {
    if (!detailDeposit || !onForceComplete || !data.text) return;

    setForceCompletePopupOpen(false);
    setActionLoading(true);
    try {
      await onForceComplete(detailDeposit.referenceNumber, data.text);
      // Refresh detail after action
      if (onGetDepositDetails) {
        const updated = await onGetDepositDetails(detailDeposit.referenceNumber);
        setDetailDeposit(updated);
      }
    } catch (error) {
      console.error('Failed to force complete deposit:', error);
    } finally {
      setActionLoading(false);
    }
  }, [detailDeposit, onForceComplete, onGetDepositDetails]);

  // Close detail sheet
  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailDeposit(null);
  }, []);

  // Get display label for deposit
  const getDepositLabel = (deposit: AdminDepositSummary) => {
    return `${deposit.amount} \u20BC`;
  };

  // Get search highlights for deposit
  const getDepositHighlights = (deposit: AdminDepositSummary): string[] | undefined => {
    if (!searchQuery) return undefined;
    const highlights = findSearchHighlights(
      {
        referenceNumber: deposit.referenceNumber,
        telegramId: deposit.telegramId,
        amount: `${deposit.amount} \u20BC`,
      },
      searchQuery
    );
    return highlights || undefined;
  };

  // Count active filters
  const activeFilterCount = Object.values(filterPanel.activeFilters).reduce(
    (sum, arr) => sum + arr.length,
    0
  );

  return (
    <>
      <ListLayout
        title="Deposits"
        count={filteredDeposits.length}
        countLabel="deposits"
        searchQuery={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search deposits..."
        onFilterClick={filterPanel.toggleCategoryBox}
        isFilterActive={filterPanel.isCategoryBoxOpen}
        activeFilterCount={activeFilterCount}
        sortOptions={sortOptions}
        sortValue={sortValue}
        onSortChange={handleSort}
        menuOptions={adminMenuOptions}
        menuValue="deposits"
        onMenuSelect={(page) => onNavigate?.(page)}
        isLoading={isLoading}
        loadingCount={8}
        isEmpty={filteredDeposits.length === 0 && !isLoading}
        scrollContainerRef={scrollContainerRef}
        emptyContent={
          <div className="text-center">
            <ArrowDownToLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-400 text-sm">
              {searchQuery ? 'No deposits match your search' : 'No deposits found'}
            </p>
          </div>
        }
        filterContent={
          <FilterDropdowns
            categories={filterCategories}
            activeFilters={filterPanel.activeFilters}
            onFiltersChange={filterPanel.setActiveFilters}
            isCategoryBoxOpen={filterPanel.isCategoryBoxOpen}
            onCategoryBoxClose={filterPanel.closeCategoryBox}
            expandedCategories={filterPanel.expandedCategories}
            onExpandCategory={filterPanel.expandCategory}
            onCollapseCategory={filterPanel.collapseCategory}
          />
        }
      >
        <InfoCardList>
          {filteredDeposits.map((deposit) => (
            <InfoCard
              key={deposit.referenceNumber}
              label={getDepositLabel(deposit)}
              type="deposit"
              searchHighlights={getDepositHighlights(deposit)}
              onClick={() => handleNavigateDetail(deposit.referenceNumber)}
            />
          ))}
        </InfoCardList>

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />
      </ListLayout>

      {/* Detail Bottom Sheet - Full Screen */}
      <BottomSheet
        isOpen={detailOpen}
        onClose={handleCloseDetail}
        height="full"
      >
        {detailDeposit && (
          <InfoCardDetail
            title={detailDeposit.referenceNumber}
            subtitle={detailDeposit.status.charAt(0).toUpperCase() + detailDeposit.status.slice(1)}
            status={detailDeposit.status}
            boxes={buildDepositBoxes({
              deposit: detailDeposit,
              onForceCompleteClick: onForceComplete ? handleForceCompleteClick : undefined,
              forceCompleteLoading: actionLoading,
              onEditFinancialsClick: onEditFinancials ? handleEditFinancialsClick : undefined,
              editLoading,
            })}
            onClose={handleCloseDetail}
            isLoading={detailLoading}
          />
        )}
        {detailLoading && !detailDeposit && (
          <div className="p-4 space-y-4">
            <div className="h-8 w-2/3 bg-slate-200 rounded-lg animate-pulse" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Force Complete Confirmation Popup */}
      <PopupMessage
        isOpen={forceCompletePopupOpen}
        title="Force Complete"
        message="Are you sure you want to force complete this deposit? This will credit the user's balance without payment verification."
        showTextInput
        textPlaceholder="Enter reason for force complete..."
        textRequired
        minTextLength={15}
        proceedLabel="Force Complete"
        proceedVariant="success"
        declineLabel="Cancel"
        onDecline={() => setForceCompletePopupOpen(false)}
        onProceed={handleForceCompleteConfirm}
        isLoading={actionLoading}
      />

      {/* Edit Financials Popup */}
      <PopupMessage
        isOpen={editPopupOpen}
        title="Edit Financials"
        message="Update the conversion and commission details for this deposit."
        proceedLabel="Save Changes"
        proceedVariant="primary"
        declineLabel="Cancel"
        onDecline={() => setEditPopupOpen(false)}
        onProceed={handleEditFinancialsConfirm}
        isLoading={editLoading}
        proceedDisabled={!editFormValues.usdtAmount && !editFormValues.exchangeRate && !editFormValues.commission}
        customContent={
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">USDT Amount</label>
              <input
                type="number"
                step="0.01"
                value={editFormValues.usdtAmount}
                onChange={(e) => setEditFormValues((prev) => ({ ...prev, usdtAmount: e.target.value }))}
                placeholder="e.g. 29.41"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Exchange Rate</label>
              <input
                type="number"
                step="0.0001"
                value={editFormValues.exchangeRate}
                onChange={(e) => setEditFormValues((prev) => ({ ...prev, exchangeRate: e.target.value }))}
                placeholder="e.g. 1.7"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Commission %</label>
              <input
                type="number"
                step="0.1"
                value={editFormValues.commission}
                onChange={(e) => setEditFormValues((prev) => ({ ...prev, commission: e.target.value }))}
                placeholder="e.g. 5"
                className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
        }
      />
    </>
  );
}
